import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import api from '../../services/api';
import { showConfirmation, showError, showSuccess } from '../../services/alert';
import './AssinaturasClientes.css';

const hojeIso = () => new Date().toISOString().slice(0, 10);

const addDiasIso = (dias) => {
  const data = new Date();
  data.setDate(data.getDate() + dias);
  return data.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
};

const statusClass = {
  Liberado: 'text-bg-success',
  Vencido: 'text-bg-warning',
  Bloqueado: 'text-bg-danger'
};

function AssinaturasClientes() {
  const [clientes, setClientes] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [approval, setApproval] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [creating, setCreating] = useState(false);
  const [catalogoModulos, setCatalogoModulos] = useState([]);
  const [clienteModulos, setClienteModulos] = useState(null);
  const [savingModules, setSavingModules] = useState(false);
  const [novoCliente, setNovoCliente] = useState({
    nome: '', nomeAdministrador: '', emailAdministrador: '', senhaAdministrador: '',
    mensalidadeVenceEm: addDiasIso(30), observacao: ''
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const [response, modulosResponse, solicitacoesResponse] = await Promise.all([
        api.get('/assinaturas/clientes'), api.get('/assinaturas/modulos'), api.get('/solicitacoes-teste')
      ]);
      setClientes(Array.isArray(response.data) ? response.data : []);
      setCatalogoModulos(Array.isArray(modulosResponse.data) ? modulosResponse.data : []);
      setSolicitacoes(Array.isArray(solicitacoesResponse.data) ? solicitacoesResponse.data : []);
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível carregar assinaturas.');
    } finally {
      setLoading(false);
    }
  };

  const alternarModulo = (moduloId) => setClienteModulos((atual) => ({
    ...atual,
    modulos: atual.modulos.includes(moduloId)
      ? atual.modulos.filter((id) => id !== moduloId)
      : [...atual.modulos, moduloId]
  }));

  const salvarModulos = async () => {
    if (!clienteModulos) return;
    setSavingModules(true);
    try {
      await api.put(`/assinaturas/clientes/${encodeURIComponent(clienteModulos.tenantId)}/modulos`, { modulos: clienteModulos.modulos });
      showSuccess('Módulos do cliente atualizados.');
      setClienteModulos(null);
      carregar();
    } catch (error) { showError(error.response?.data?.message || 'Não foi possível atualizar os módulos.'); }
    finally { setSavingModules(false); }
  };

  useEffect(() => {
    carregar();
  }, []);

  const aprovarSolicitacao = async (event) => {
    event.preventDefault();
    if (!approval || approval.senhaInicial.length < 8) { showError('A senha inicial deve ter pelo menos 8 caracteres.'); return; }
    setProcessingRequest(true);
    try {
      const { data } = await api.post(`/solicitacoes-teste/${approval.id}/aprovar`, { senhaInicial: approval.senhaInicial, acessoAte: approval.acessoAte, modulos: approval.modulos, reuniaoConcluida: approval.reuniaoConcluida });
      showSuccess(`${data.message} Cliente ${data.tenantId}, acesso: ${data.email}`);
      setApproval(null); carregar();
    } catch (error) { showError(error.response?.data?.message || 'Não foi possível liberar o acesso.'); }
    finally { setProcessingRequest(false); }
  };

  const rejeitarSolicitacao = async (solicitacao) => {
    const confirmed = await showConfirmation({ title: 'Rejeitar solicitação?', text: `O pré-cadastro da ${solicitacao.nomeFazenda} será encerrado.`, confirmText: 'Rejeitar' });
    if (!confirmed) return;
    setProcessingRequest(true);
    try { await api.post(`/solicitacoes-teste/${solicitacao.id}/rejeitar`, { motivo: 'Solicitação não aprovada após análise da equipe.' }); showSuccess('Solicitação rejeitada.'); carregar(); }
    catch (error) { showError(error.response?.data?.message || 'Não foi possível rejeitar a solicitação.'); }
    finally { setProcessingRequest(false); }
  };

  const criarCliente = async (event) => {
    event.preventDefault();
    if (novoCliente.senhaAdministrador.length < 8) {
      showError('A senha inicial deve ter pelo menos 8 caracteres.');
      return;
    }
    try {
      setCreating(true);
      const response = await api.post('/assinaturas/clientes', novoCliente);
      showSuccess(`${response.data.message} Código do cliente: ${response.data.tenantId}`);
      setNovoCliente({ nome: '', nomeAdministrador: '', emailAdministrador: '', senhaAdministrador: '', mensalidadeVenceEm: addDiasIso(30), observacao: '' });
      carregar();
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível cadastrar o cliente.');
    } finally {
      setCreating(false);
    }
  };

  const atualizarCliente = (tenantId, campo, valor) => {
    setClientes((atuais) => atuais.map((cliente) => (
      cliente.tenantId === tenantId ? { ...cliente, [campo]: valor } : cliente
    )));
  };

  const salvar = async (cliente) => {
    setSavingId(cliente.tenantId);
    try {
      await api.put(`/assinaturas/clientes/${encodeURIComponent(cliente.tenantId)}`, {
        ativo: Boolean(cliente.ativo),
        mensalidadeVenceEm: cliente.mensalidadeVenceEm || null,
        bloqueadoPorInadimplencia: Boolean(cliente.bloqueadoPorInadimplencia),
        observacaoAssinatura: cliente.observacaoAssinatura || null
      });
      showSuccess('Assinatura atualizada.');
      carregar();
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível salvar assinatura.');
    } finally {
      setSavingId('');
    }
  };

  const bloquear = async (cliente) => {
    setSavingId(cliente.tenantId);
    try {
      await api.post(`/assinaturas/clientes/${encodeURIComponent(cliente.tenantId)}/bloquear`, {
        motivo: cliente.observacaoAssinatura || 'Bloqueio manual por inadimplência.'
      });
      showSuccess('Cliente bloqueado.');
      carregar();
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível bloquear cliente.');
    } finally {
      setSavingId('');
    }
  };

  const desbloquear = async (cliente) => {
    setSavingId(cliente.tenantId);
    try {
      await api.post(`/assinaturas/clientes/${encodeURIComponent(cliente.tenantId)}/desbloquear`, {
        novoVencimento: cliente.mensalidadeVenceEm || addDiasIso(30),
        observacao: cliente.observacaoAssinatura || 'Cliente liberado manualmente.'
      });
      showSuccess('Cliente desbloqueado.');
      carregar();
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível desbloquear cliente.');
    } finally {
      setSavingId('');
    }
  };

  const resumo = clientes.reduce((acc, item) => {
    acc.total += 1;
    if (item.status === 'Liberado') acc.liberados += 1;
    if (item.status === 'Vencido') acc.vencidos += 1;
    if (item.status === 'Bloqueado') acc.bloqueados += 1;
    return acc;
  }, { total: 0, liberados: 0, vencidos: 0, bloqueados: 0 });

  return (
    <div className="platform-admin-page container-fluid py-4">
      <Header />

      <div className="platform-hero mb-4">
        <div>
          <span className="platform-kicker">CultivaMais Control</span>
          <h2>Gestão da plataforma</h2>
          <p>Clientes, acessos e assinaturas em um único ambiente.</p>
        </div>
        <div className="platform-live"><span /> Operação online</div>
      </div>

      <div className="card platform-card trial-requests-card mb-4">
        <div className="card-header platform-card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3"><div className="platform-header-icon"><i className="bi bi-calendar2-check" /></div><div><h5 className="mb-0 fw-semibold">Solicitações para demonstração</h5><small>Revise os dados após a reunião e libere o ambiente de testes</small></div></div>
          <span className="request-count">{solicitacoes.filter((item) => item.status === 'Pendente').length} pendentes</span>
        </div>
        <div className="card-body"><div className="trial-request-list">
          {solicitacoes.filter((item) => item.status === 'Pendente').map((item) => <article className="trial-request" key={item.id}>
            <div className="trial-request-main"><span className={`request-id ${item.tipoInteresse === 'Contratacao' ? 'immediate' : ''}`}>{item.tipoInteresse === 'Contratacao' ? 'Contratação imediata' : 'Teste'} · Solicitação #{item.id}</span><h6>{item.nomeFazenda}</h6><p><i className="bi bi-geo-alt" /> {item.cidade} - {item.estado} {item.areaHa ? `· ${item.areaHa} ha` : ''}</p>{item.emailNotificadoEmUtc ? <small className="request-whatsapp sent"><i className="bi bi-envelope-check" /> Aviso enviado por e-mail</small> : <small className="request-whatsapp failed" title={item.emailNotificacaoErro || ''}><i className="bi bi-exclamation-triangle" /> E-mail pendente</small>}</div>
            <div className="trial-request-contact"><strong>{item.nomeResponsavel}</strong><span>{item.email}</span><small>{item.telefone} · {item.cnpj.length === 11 ? 'CPF' : 'CNPJ'} {item.cnpj.length === 11 ? item.cnpj.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : item.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}</small></div>
            <div className="trial-request-actions"><button type="button" className="btn btn-outline-danger btn-sm" disabled={processingRequest} onClick={() => rejeitarSolicitacao(item)}>Rejeitar</button><button type="button" className="btn btn-success btn-sm" onClick={() => setApproval({ id: item.id, nomeFazenda: item.nomeFazenda, email: item.email, senhaInicial: '', acessoAte: addDiasIso(15), modulos: catalogoModulos.map((module) => module.id), reuniaoConcluida: false })}>Liberar acesso</button></div>
          </article>)}
          {!loading && solicitacoes.filter((item) => item.status === 'Pendente').length === 0 && <div className="request-empty"><i className="bi bi-check2-circle" /><strong>Nenhuma solicitação aguardando liberação</strong><span>Novos pré-cadastros aparecerão aqui.</span></div>}
        </div></div>
      </div>

      <div className="card platform-card platform-create-card mb-4">
        <div className="card-header platform-card-header">
          <div className="platform-header-icon"><i className="bi bi-building-add" /></div>
          <div><h5 className="mb-0 fw-semibold">Novo cliente</h5><small>Provisionamento automático do ambiente e administrador inicial</small></div>
        </div>
        <div className="card-body">
          <form className="row g-3" onSubmit={criarCliente}>
            <div className="col-md-6"><label className="form-label fw-semibold">Nome do cliente</label><input className="form-control" value={novoCliente.nome} onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })} required /></div>
            <div className="col-md-6"><label className="form-label fw-semibold">Vencimento inicial</label><input type="date" className="form-control" value={novoCliente.mensalidadeVenceEm} onChange={(e) => setNovoCliente({ ...novoCliente, mensalidadeVenceEm: e.target.value })} required /></div>
            <div className="col-md-4"><label className="form-label fw-semibold">Administrador do cliente</label><input className="form-control" value={novoCliente.nomeAdministrador} onChange={(e) => setNovoCliente({ ...novoCliente, nomeAdministrador: e.target.value })} required /></div>
            <div className="col-md-4"><label className="form-label fw-semibold">E-mail de acesso</label><input type="email" className="form-control" value={novoCliente.emailAdministrador} onChange={(e) => setNovoCliente({ ...novoCliente, emailAdministrador: e.target.value })} required /></div>
            <div className="col-md-4"><label className="form-label fw-semibold">Senha inicial</label><input type="password" minLength="8" className="form-control" value={novoCliente.senhaAdministrador} onChange={(e) => setNovoCliente({ ...novoCliente, senhaAdministrador: e.target.value })} required /></div>
            <div className="col-md-9"><label className="form-label fw-semibold">Observação</label><input className="form-control" value={novoCliente.observacao} onChange={(e) => setNovoCliente({ ...novoCliente, observacao: e.target.value })} placeholder="Plano, negociação ou observação interna" /></div>
            <div className="col-md-3 d-flex align-items-end"><button className="btn btn-success w-100" type="submit" disabled={creating}><i className="bi bi-building-add me-2" />{creating ? 'Criando...' : 'Criar cliente'}</button></div>
          </form>
        </div>
      </div>

      <div className="card platform-card mb-4">
        <div className="card-header platform-card-header d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0 fw-semibold">Assinaturas e bloqueio de clientes</h5>
            <small>Controle de vencimento, inadimplência e liberação por cliente</small>
          </div>
          <button type="button" className="btn btn-light btn-sm text-success fw-semibold" onClick={carregar} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Atualizar
          </button>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-6 col-xl-3">
              <div className="platform-stat platform-stat-total h-100">
                <span className="text-muted small">Clientes</span>
                <div className="fs-4 fw-bold">{resumo.total}</div>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="platform-stat platform-stat-success h-100">
                <span className="text-muted small">Liberados</span>
                <div className="fs-4 fw-bold text-success">{resumo.liberados}</div>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="platform-stat platform-stat-warning h-100">
                <span className="text-muted small">Vencidos</span>
                <div className="fs-4 fw-bold text-warning">{resumo.vencidos}</div>
              </div>
            </div>
            <div className="col-6 col-xl-3">
              <div className="platform-stat platform-stat-danger h-100">
                <span className="text-muted small">Bloqueados</span>
                <div className="fs-4 fw-bold text-danger">{resumo.bloqueados}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card platform-card platform-table-card">
        <div className="card-header bg-white">
          <h6 className="mb-0 fw-semibold">Clientes cadastrados</h6>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tenant</th>
                  <th>Status</th>
                  <th>Vencimento</th>
                  <th>Dias</th>
                  <th>Ativo</th>
                  <th>Bloquear</th>
                  <th>Observação</th>
                  <th>Módulos</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente) => {
                  const saving = savingId === cliente.tenantId;
                  return (
                    <tr key={cliente.tenantId}>
                      <td className="fw-semibold">{cliente.nome}</td>
                      <td><code>{cliente.tenantId}</code></td>
                      <td>
                        <span className={`badge ${statusClass[cliente.status] || 'text-bg-secondary'}`}>
                          {cliente.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 150 }}>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={cliente.mensalidadeVenceEm ? cliente.mensalidadeVenceEm.slice(0, 10) : ''}
                          onChange={(e) => atualizarCliente(cliente.tenantId, 'mensalidadeVenceEm', e.target.value)}
                        />
                        <small className="text-muted">Atual: {formatDate(cliente.mensalidadeVenceEm)}</small>
                      </td>
                      <td>{cliente.diasParaVencer ?? '-'}</td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(cliente.ativo)}
                          onChange={(e) => atualizarCliente(cliente.tenantId, 'ativo', e.target.checked)}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(cliente.bloqueadoPorInadimplencia)}
                          onChange={(e) => atualizarCliente(cliente.tenantId, 'bloqueadoPorInadimplencia', e.target.checked)}
                        />
                      </td>
                      <td style={{ minWidth: 220 }}>
                        <input
                          className="form-control form-control-sm"
                          value={cliente.observacaoAssinatura || ''}
                          onChange={(e) => atualizarCliente(cliente.tenantId, 'observacaoAssinatura', e.target.value)}
                          placeholder="Motivo ou observação"
                        />
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <button type="button" className="btn btn-outline-success btn-sm text-nowrap" onClick={() => setClienteModulos({ tenantId: cliente.tenantId, nome: cliente.nome, modulos: [...(cliente.modulos || [])] })}>
                          <i className="bi bi-grid me-1" />{cliente.modulos?.length || 0} liberados
                        </button>
                      </td>
                      <td className="text-end">
                        <div className="platform-actions">
                          <button type="button" className="btn btn-outline-success" onClick={() => salvar(cliente)} disabled={saving}>
                            Salvar
                          </button>
                          <button type="button" className="btn btn-outline-danger" onClick={() => bloquear(cliente)} disabled={saving}>
                            Bloquear
                          </button>
                          <button type="button" className="btn btn-outline-primary" onClick={() => desbloquear(cliente)} disabled={saving}>
                            Liberar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && clientes.length === 0 && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      Nenhum cliente encontrado.
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      Carregando clientes...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="alert alert-warning mt-3 mb-0">
            <strong>Regra:</strong> cliente com vencimento anterior a {formatDate(hojeIso())} ou marcado como bloqueado não consegue operar o sistema.
          </div>
        </div>
      </div>
      {clienteModulos && <div className="module-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setClienteModulos(null)}>
        <section className="module-modal" role="dialog" aria-modal="true" aria-labelledby="module-modal-title">
          <div className="module-modal-header"><div><span className="platform-kicker">Plano do cliente</span><h4 id="module-modal-title">Módulos de {clienteModulos.nome}</h4><p>Libere somente os recursos contratados. A alteração vale imediatamente.</p></div><button type="button" className="btn-close" aria-label="Fechar" onClick={() => setClienteModulos(null)} /></div>
          <div className="module-modal-tools"><span>{clienteModulos.modulos.length} de {catalogoModulos.length} módulos liberados</span><div><button type="button" className="btn btn-link btn-sm" onClick={() => setClienteModulos({ ...clienteModulos, modulos: catalogoModulos.map((m) => m.id) })}>Selecionar todos</button><button type="button" className="btn btn-link btn-sm text-danger" onClick={() => setClienteModulos({ ...clienteModulos, modulos: [] })}>Limpar</button></div></div>
          <div className="module-grid">{catalogoModulos.map((modulo) => { const ativo = clienteModulos.modulos.includes(modulo.id); return <button key={modulo.id} type="button" className={`module-option ${ativo ? 'active' : ''}`} onClick={() => alternarModulo(modulo.id)}><span className="module-option-icon"><i className={`bi ${modulo.icone}`} /></span><span><strong>{modulo.nome}</strong><small>{modulo.descricao}</small></span><i className={`bi ${ativo ? 'bi-check-circle-fill' : 'bi-circle'} module-check`} /></button>; })}</div>
          <div className="module-modal-footer"><button type="button" className="btn btn-outline-secondary" onClick={() => setClienteModulos(null)} disabled={savingModules}>Cancelar</button><button type="button" className="btn btn-success" onClick={salvarModulos} disabled={savingModules}>{savingModules ? 'Salvando...' : 'Salvar módulos'}</button></div>
        </section>
      </div>}
      {approval && <div className="module-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setApproval(null)}><form className="request-approval-modal" onSubmit={aprovarSolicitacao}>
        <div className="module-modal-header"><div><span className="platform-kicker">Liberação controlada</span><h4>Acesso de {approval.nomeFazenda}</h4><p>Crie o cliente, o administrador e a fazenda inicial após a reunião.</p></div><button type="button" className="btn-close" aria-label="Fechar" onClick={() => setApproval(null)} /></div>
        <div className="request-approval-body"><div className="request-access-email"><i className="bi bi-envelope-check" /><span><small>E-mail do administrador</small><strong>{approval.email}</strong></span></div><div className="row g-3"><div className="col-md-6"><label className="form-label">Senha inicial *</label><input type="password" minLength="8" className="form-control" value={approval.senhaInicial} onChange={(e) => setApproval({ ...approval, senhaInicial: e.target.value })} required /><small className="text-muted">Mínimo de 8 caracteres</small></div><div className="col-md-6"><label className="form-label">Acesso liberado até *</label><input type="date" className="form-control" value={approval.acessoAte} min={hojeIso()} onChange={(e) => setApproval({ ...approval, acessoAte: e.target.value })} required /></div></div><div className="request-module-summary"><i className="bi bi-grid" /><span><strong>{approval.modulos.length} módulos serão liberados</strong><small>Você poderá alterar os módulos depois na assinatura do cliente.</small></span></div><label className="meeting-confirmation"><input type="checkbox" checked={approval.reuniaoConcluida} onChange={(e) => setApproval({ ...approval, reuniaoConcluida: e.target.checked })} required /><span><strong>Confirmo que a reunião foi concluída</strong><small>O sistema não permitirá a liberação sem esta confirmação.</small></span></label></div>
        <div className="module-modal-footer"><button type="button" className="btn btn-outline-secondary" onClick={() => setApproval(null)} disabled={processingRequest}>Cancelar</button><button type="submit" className="btn btn-success" disabled={processingRequest || !approval.reuniaoConcluida}>{processingRequest ? 'Liberando...' : 'Confirmar e liberar'}</button></div>
      </form></div>}
    </div>
  );
}

export default AssinaturasClientes;
